import type { SupabaseClient } from '@supabase/supabase-js'
import type { Database } from '@/types/database'
import { TIPOS_PROMO_PERMITIDOS, esTipoPromo, type TipoPromo } from '@/emails/components/promoInstagram.utils'

// Types from generated database types
type ConfigEstudioRow = Database['public']['Tables']['configuracion_estudio']['Row']
type ConfigEstudioUpdate = Database['public']['Tables']['configuracion_estudio']['Update']
type PreferenciasRow = Database['public']['Tables']['preferencias_usuario']['Row']

// Public interfaces (subset of DB fields used by the UI)
export interface ConfiguracionEstudio {
  id?: string
  nombre: string
  cuit: string
  direccion: string
  telefono: string
  email: string
  responsable_nombre?: string | null
  matricula_profesional?: string | null
  sitio_web?: string | null
  texto_legal_presupuestos?: string | null
  validez_dias_default?: number | null
  // Extra fields from DB (not yet exposed in UI)
  condicion_iva?: string | null
  iibb_condicion?: string | null
  iibb_numero?: string | null
  inicio_actividades?: string | null
  texto_footer_comprobantes?: string | null
  // ORR-89: firma configurable en emails (por estudio)
  firma_email_html?: string | null
  firma_email_imagen_url?: string | null
  // ORR-155: sello del estudio (campo separado de la firma)
  firma_email_sello_url?: string | null
  // ORR-156/158: pie del mail = identidad del estudio (banner + redes)
  logo_url?: string | null
  instagram_url?: string | null
  whatsapp?: string | null
  // ORR-340: datos para el pago, en el pie de los avisos de cobranza. Van en la config y no
  // fijos en el template para que cambiar de cuenta no requiera un deploy.
  banco?: string | null
  cbu?: string | null
  alias_cbu?: string | null
  tipo_cuenta?: string | null
  numero_cuenta?: string | null
  titular_cuenta?: string | null
  titular_cuil?: string | null
}

export interface PreferenciasUsuario {
  id?: string
  user_id: string
  tema: 'light' | 'dark' | 'system'
  email_nuevo_cliente: boolean
  email_nuevo_movimiento: boolean
  email_recordatorio_vencimiento: boolean
  email_recordatorio_lote_deudas: boolean
}

/** Bloque «Seguinos en Instagram» de los mails a clientes (plan-promo-instagram-mails). */
export interface PromoInstagramConfig {
  activa: boolean
  tipos: TipoPromo[]
}

/** Lo que tiene un estudio que nunca tocó la sección: apagado y con todos los tipos permitidos. */
export const PROMO_INSTAGRAM_DEFAULT: PromoInstagramConfig = {
  activa: false,
  tipos: [...TIPOS_PROMO_PERMITIDOS],
}

/** Error de un documento que no se genera porque los datos del estudio no están disponibles. */
export class ConfiguracionEstudioNoDisponibleError extends Error {
  constructor(mensaje: string) {
    super(mensaje)
    this.name = 'ConfiguracionEstudioNoDisponibleError'
  }
}

/**
 * Devuelve la configuración del estudio para armar un documento que sale al cliente.
 *
 * Recibe el resultado de `useConfiguracionEstudio`. `null` significa que la organización no
 * configuró su estudio, y se devuelve tal cual. `undefined` significa que la query falló o no
 * terminó de cargar: en ese caso lanza `ConfiguracionEstudioNoDisponibleError`, para que el
 * documento no salga con un nombre genérico.
 */
export const exigirConfiguracionEstudio = (query: {
  data: ConfiguracionEstudio | null | undefined
  isError: boolean
}): ConfiguracionEstudio | null => {
  if (query.data !== undefined) return query.data
  throw new ConfiguracionEstudioNoDisponibleError(
    query.isError
      ? 'No se pudieron cargar los datos del estudio, así que el documento no se generó. Reintentá en unos segundos.'
      : 'Los datos del estudio todavía se están cargando. Reintentá en unos segundos.',
  )
}

/**
 * Servicio para gestión de configuraciones.
 *
 * IMPORTANTE: Todos los métodos reciben el cliente Supabase autenticado como primer parámetro.
 *
 * @example
 * const { supabase } = useSupabase()
 * const config = await configuracionService.obtenerConfiguracion(supabase!)
 */
export const configuracionService = {
  // Configuracion del estudio
  async obtenerConfiguracion(client: SupabaseClient<Database>, organizacionId: string): Promise<ConfiguracionEstudio | null> {
    const { data, error } = await client
      .from('configuracion_estudio')
      .select('*')
      .eq('organizacion_id', organizacionId)
      .limit(1)

    // Propagar el fallo en vez de devolver null. Para TanStack Query un null es un ÉXITO: no
    // reintenta y lo cachea con el staleTime de 1h de useConfiguracionEstudio. Un error de red
    // en la primera carga dejaba la config del estudio en null durante una hora, y todo lo que
    // la consume caía a sus defaults sin avisar — ORR-310, contratos enviados a clientes con
    // "Mi Estudio Contable" y "el titular" en vez de los datos reales.
    if (error) {
      console.error('Error obteniendo configuracion:', error)
      throw new Error(`No se pudo obtener la configuración del estudio: ${error.message}`)
    }

    // null acá sí es un resultado legítimo: la organización todavía no configuró su estudio.
    const row = data?.[0] as ConfigEstudioRow | undefined
    if (!row) return null

    return {
      id: row.id,
      nombre: row.nombre ?? '',
      cuit: row.cuit ?? '',
      direccion: row.direccion ?? '',
      telefono: row.telefono ?? '',
      email: row.email ?? '',
      responsable_nombre: row.responsable_nombre,
      matricula_profesional: row.matricula_profesional,
      sitio_web: row.sitio_web,
      texto_legal_presupuestos: row.texto_legal_presupuestos,
      validez_dias_default: row.validez_dias_default,
      condicion_iva: row.condicion_iva,
      iibb_condicion: row.iibb_condicion,
      iibb_numero: row.iibb_numero,
      inicio_actividades: row.inicio_actividades,
      texto_footer_comprobantes: row.texto_footer_comprobantes,
      firma_email_html: row.firma_email_html,
      firma_email_imagen_url: row.firma_email_imagen_url,
      firma_email_sello_url: row.firma_email_sello_url,
      logo_url: row.logo_url,
      instagram_url: row.instagram_url,
      whatsapp: row.whatsapp,
      // ORR-366 — los 7 campos de cobro faltaban acá y en el payload de `guardarConfiguracion`,
      // así que el formulario los mostraba siempre vacíos y guardar no los escribía nunca. El
      // botón se habilitaba, la mutación resolvía OK y el dato se perdía entre el form y la base:
      // Lisandro cargó el CBU el 02/09 y los 127 avisos de esa semana salieron sin datos de pago.
      banco: row.banco,
      cbu: row.cbu,
      alias_cbu: row.alias_cbu,
      tipo_cuenta: row.tipo_cuenta,
      numero_cuenta: row.numero_cuenta,
      titular_cuenta: row.titular_cuenta,
      titular_cuil: row.titular_cuil,
    }
  },

  async guardarConfiguracion(client: SupabaseClient<Database>, organizacionId: string, config: ConfiguracionEstudio): Promise<ConfiguracionEstudio | null> {
    const existing = await this.obtenerConfiguracion(client, organizacionId)

    const payload: ConfigEstudioUpdate = {
      nombre: config.nombre,
      cuit: config.cuit,
      direccion: config.direccion,
      telefono: config.telefono,
      email: config.email,
      responsable_nombre: config.responsable_nombre,
      matricula_profesional: config.matricula_profesional,
      sitio_web: config.sitio_web,
      texto_legal_presupuestos: config.texto_legal_presupuestos,
      validez_dias_default: config.validez_dias_default,
      condicion_iva: config.condicion_iva,
      iibb_condicion: config.iibb_condicion,
      iibb_numero: config.iibb_numero,
      inicio_actividades: config.inicio_actividades,
      firma_email_html: config.firma_email_html,
      firma_email_imagen_url: config.firma_email_imagen_url,
      firma_email_sello_url: config.firma_email_sello_url,
      // ORR-366 — ver el comentario en `obtenerConfiguracion`. Van en el payload y no en un update
      // aparte porque el formulario que los edita es el mismo que edita el resto.
      banco: config.banco,
      cbu: config.cbu,
      alias_cbu: config.alias_cbu,
      tipo_cuenta: config.tipo_cuenta,
      numero_cuenta: config.numero_cuenta,
      titular_cuenta: config.titular_cuenta,
      titular_cuil: config.titular_cuil,
    }

    if (existing?.id) {
      const { data, error } = await client
        .from('configuracion_estudio')
        .update(payload)
        .eq('id', existing.id)
        .select()
        .single()

      if (error) throw error
      return data as unknown as ConfiguracionEstudio
    } else {
      const { data, error } = await client
        .from('configuracion_estudio')
        .insert({ ...payload, organizacion_id: organizacionId })
        .select()
        .single()

      if (error) throw error
      return data as unknown as ConfiguracionEstudio
    }
  },

  async actualizarFirmaEmail(
    client: SupabaseClient<Database>,
    organizacionId: string,
    firma: {
      firma_email_html: string | null
      firma_email_imagen_url: string | null
      firma_email_sello_url: string | null
      logo_url: string | null
      instagram_url: string | null
      whatsapp: string | null
    },
  ): Promise<void> {
    const existing = await this.obtenerConfiguracion(client, organizacionId)

    if (existing?.id) {
      const { error } = await client
        .from('configuracion_estudio')
        .update({
          firma_email_html: firma.firma_email_html,
          firma_email_imagen_url: firma.firma_email_imagen_url,
          firma_email_sello_url: firma.firma_email_sello_url,
          logo_url: firma.logo_url,
          instagram_url: firma.instagram_url,
          whatsapp: firma.whatsapp,
        })
        .eq('id', existing.id)

      if (error) throw error
    } else {
      // Si no hay configuracion_estudio aun, crearla con datos minimos
      const { error } = await client
        .from('configuracion_estudio')
        .insert({
          organizacion_id: organizacionId,
          firma_email_html: firma.firma_email_html,
          firma_email_imagen_url: firma.firma_email_imagen_url,
          firma_email_sello_url: firma.firma_email_sello_url,
          logo_url: firma.logo_url,
          instagram_url: firma.instagram_url,
          whatsapp: firma.whatsapp,
        })

      if (error) throw error
    }
  },

  async obtenerPromoInstagram(client: SupabaseClient<Database>, organizacionId: string): Promise<PromoInstagramConfig> {
    const { data, error } = await client
      .from('configuracion_estudio')
      .select('promo_instagram_activa, promo_instagram_tipos')
      .eq('organizacion_id', organizacionId)
      .limit(1)
    if (error) throw new Error(`No se pudo leer la configuración de Instagram en los mails: ${error.message}`)

    const row = data?.[0]
    if (!row) return PROMO_INSTAGRAM_DEFAULT
    return {
      activa: row.promo_instagram_activa === true,
      tipos: (row.promo_instagram_tipos ?? []).filter(esTipoPromo),
    }
  },

  async guardarPromoInstagram(
    client: SupabaseClient<Database>,
    organizacionId: string,
    config: PromoInstagramConfig,
  ): Promise<void> {
    const columnas: ConfigEstudioUpdate = {
      promo_instagram_activa: config.activa,
      // Solo tipos permitidos y sin repetir: la base tiene un CHECK con la misma lista.
      promo_instagram_tipos: TIPOS_PROMO_PERMITIDOS.filter((t) => config.tipos.includes(t)),
    }
    const existing = await this.obtenerConfiguracion(client, organizacionId)

    const { error } = existing?.id
      ? await client
          .from('configuracion_estudio')
          .update(columnas)
          .eq('id', existing.id)
          .eq('organizacion_id', organizacionId)
      : await client
          .from('configuracion_estudio')
          .insert({ ...columnas, organizacion_id: organizacionId })
    if (error) throw error
  },

  // Preferencias de usuario
  async obtenerPreferencias(client: SupabaseClient<Database>, userId: string): Promise<PreferenciasUsuario | null> {
    const { data, error } = await client
      .from('preferencias_usuario')
      .select('*')
      .eq('user_id', userId)
      .limit(1)

    if (error) {
      console.error('Error obteniendo preferencias:', error)
      return null
    }

    const row = data?.[0] as PreferenciasRow | undefined
    if (!row) return null

    return {
      id: row.id,
      user_id: row.user_id,
      tema: (row.tema as PreferenciasUsuario['tema']) ?? 'system',
      email_nuevo_cliente: row.email_nuevo_cliente ?? true,
      email_nuevo_movimiento: row.email_nuevo_movimiento ?? false,
      email_recordatorio_vencimiento: row.email_recordatorio_vencimiento ?? true,
      email_recordatorio_lote_deudas: row.email_recordatorio_lote_deudas ?? true,
    }
  },

  async guardarPreferencias(client: SupabaseClient<Database>, prefs: Omit<PreferenciasUsuario, 'id'>): Promise<PreferenciasUsuario | null> {
    const { data, error } = await client
      .from('preferencias_usuario')
      .upsert(
        {
          user_id: prefs.user_id,
          tema: prefs.tema,
          email_nuevo_cliente: prefs.email_nuevo_cliente,
          email_nuevo_movimiento: prefs.email_nuevo_movimiento,
          email_recordatorio_vencimiento: prefs.email_recordatorio_vencimiento,
          email_recordatorio_lote_deudas: prefs.email_recordatorio_lote_deudas,
        },
        { onConflict: 'user_id' }
      )
      .select()
      .single()

    if (error) throw error
    return data as unknown as PreferenciasUsuario
  },
}
